import React, { useState, useEffect } from 'react';
import { 
  Award, Plus, Edit2, Trash2, DollarSign, Users, Package, 
  Check, X, Gift, Mic, Building2, Star, Tag, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import api from '../../services/api';

// Predefined sponsorship type templates
const sponsorshipTemplates = [
  { type: 'title', name: 'Title Sponsor', icon: Star, defaultPrice: 50000, defaultBenefits: ['Naming rights', 'Logo on all materials', '10 delegate passes', 'VIP lounge access', 'Speaking slot'] },
  { type: 'platinum', name: 'Platinum Sponsor', icon: Award, defaultPrice: 25000, defaultBenefits: ['Premium logo placement', '5 delegate passes', 'Exhibition booth', 'Company profile in directory'] },
  { type: 'gold', name: 'Gold Sponsor', icon: Star, defaultPrice: 15000, defaultBenefits: ['Logo on materials', '3 delegate passes', 'Small booth'] },
  { type: 'lanyard', name: 'Lanyard Sponsor', icon: Tag, defaultPrice: 10000, defaultBenefits: ['Logo on all lanyards', '2 delegate passes'] },
  { type: 'badge', name: 'Badge Sponsor', icon: Tag, defaultPrice: 8000, defaultBenefits: ['Logo on all badges', '2 delegate passes'] },
  { type: 'lunch', name: 'Lunch Sponsor', icon: Gift, defaultPrice: 12000, defaultBenefits: ['Branding at lunch area', '3 delegate passes', 'Speaking opportunity'] },
  { type: 'cocktail', name: 'Cocktail Sponsor', icon: Gift, defaultPrice: 8000, defaultBenefits: ['Branding at cocktail event', '2 delegate passes'] },
  { type: 'wifi', name: 'WiFi Sponsor', icon: Building2, defaultPrice: 5000, defaultBenefits: ['Logo on WiFi login page', '1 delegate pass'] },
  { type: 'speaker', name: 'Speaker Sponsor', icon: Mic, defaultPrice: 7000, defaultBenefits: ['Logo on speaker backdrop', '2 delegate passes'] },
  { type: 'custom', name: 'Custom Sponsorship', icon: Package, defaultPrice: 0, defaultBenefits: [] }
];

const EventSponsorshipManager = ({ event, onClose, onUpdate }) => {
  const [sponsorships, setSponsorships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingSponsorship, setEditingSponsorship] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  const [form, setForm] = useState({
    type: '',
    name: '',
    description: '',
    price: 0,
    quantity_available: 1,
    delegate_slots: 0,
    delegate_fee: 0,
    benefits: [],
    is_active: true
  });
  
  const [newBenefit, setNewBenefit] = useState('');

  useEffect(() => {
    fetchSponsorships();
  }, [event?.event_id]);

  const fetchSponsorships = async () => {
    if (!event?.event_id) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/api/events/${event.event_id}/sponsorships`);
      setSponsorships(response.data || []);
    } catch (error) {
      console.error('Error fetching sponsorships:', error);
      // Use sponsorships from event object if API fails
      setSponsorships(event.sponsorships || []);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setForm({
      type: template.type,
      name: template.name,
      description: '',
      price: template.defaultPrice,
      quantity_available: template.type === 'title' ? 1 : 5,
      delegate_slots: template.defaultBenefits.filter(b => b.includes('delegate')).length > 0 ? 2 : 0,
      delegate_fee: 0,
      benefits: [...template.defaultBenefits],
      is_active: true
    });
  };

  const handleAddBenefit = () => {
    if (newBenefit.trim()) {
      setForm({ ...form, benefits: [...form.benefits, newBenefit.trim()] });
      setNewBenefit('');
    }
  };

  const handleRemoveBenefit = (index) => {
    setForm({ ...form, benefits: form.benefits.filter((_, i) => i !== index) });
  };

  const handleSave = async () => {
    if (!form.name || form.price < 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingSponsorship) {
        await api.put(
          `/api/events/${event.event_id}/sponsorships/${editingSponsorship.sponsorship_id}`,
          form
        );
        toast.success('Sponsorship updated successfully');
      } else {
        await api.post(`/api/events/${event.event_id}/sponsorships`, form);
        toast.success('Sponsorship added successfully');
      }
      
      fetchSponsorships();
      resetForm();
      setShowAddDialog(false);
      onUpdate?.();
    } catch (error) {
      console.error('Error saving sponsorship:', error);
      toast.error('Failed to save sponsorship');
    }
  };

  const handleDelete = async (sponsorshipId) => {
    if (!confirm('Are you sure you want to delete this sponsorship?')) return;
    
    try {
      await api.delete(`/api/events/${event.event_id}/sponsorships/${sponsorshipId}`);
      toast.success('Sponsorship deleted');
      fetchSponsorships();
      onUpdate?.();
    } catch (error) {
      toast.error('Failed to delete sponsorship');
    }
  };

  const resetForm = () => {
    setForm({
      type: '',
      name: '',
      description: '',
      price: 0,
      quantity_available: 1,
      delegate_slots: 0,
      delegate_fee: 0,
      benefits: [],
      is_active: true
    });
    setSelectedTemplate(null);
    setEditingSponsorship(null);
  };

  const openEditDialog = (sponsorship) => {
    setEditingSponsorship(sponsorship);
    setForm({
      type: sponsorship.type || 'custom',
      name: sponsorship.name,
      description: sponsorship.description || '',
      price: sponsorship.price || 0,
      quantity_available: sponsorship.quantity_available || 1,
      delegate_slots: sponsorship.delegate_slots || 0,
      delegate_fee: sponsorship.delegate_fee || 0,
      benefits: sponsorship.benefits || [],
      is_active: sponsorship.is_active !== false
    });
    setShowAddDialog(true);
  };

  const getTemplateIcon = (type) => {
    const template = sponsorshipTemplates.find(t => t.type === type);
    return template?.icon || Package;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Sponsorships for {event?.name}</h2>
          <p className="text-sm text-gray-500">Configure sponsorship packages for this event</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowAddDialog(true); }}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus size={16} className="mr-2" />
          Add Sponsorship
        </Button>
      </div>

      {/* Sponsorships List */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="animate-spin mr-2" />
          Loading sponsorships...
        </div>
      ) : sponsorships.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Award size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">No sponsorships configured for this event</p>
            <p className="text-sm text-gray-400 mt-2">Add sponsorship packages to allow members to sponsor this event</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sponsorships.filter(s => s.is_active !== false).map((sponsorship) => {
            const Icon = getTemplateIcon(sponsorship.type);
            const soldOut = sponsorship.quantity_sold >= sponsorship.quantity_available;
            
            return (
              <Card key={sponsorship.sponsorship_id} className={soldOut ? 'opacity-60' : ''}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                        <Icon size={20} className="text-amber-600" />
                      </div>
                      <div>
                        <CardTitle className="text-base">{sponsorship.name}</CardTitle>
                        <p className="text-xs text-gray-500">{sponsorship.type}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => openEditDialog(sponsorship)}>
                        <Edit2 size={14} />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-red-600"
                        onClick={() => handleDelete(sponsorship.sponsorship_id)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-green-600">
                        ${sponsorship.price?.toLocaleString()}
                      </span>
                      <Badge className={soldOut ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
                        {sponsorship.quantity_sold || 0} / {sponsorship.quantity_available} sold
                      </Badge>
                    </div>
                    
                    {sponsorship.delegate_slots > 0 && (
                      <div className="text-sm text-gray-600">
                        <Users size={14} className="inline mr-1" />
                        Includes {sponsorship.delegate_slots} delegate pass(es)
                        {sponsorship.delegate_fee > 0 && (
                          <span className="ml-2">(+${sponsorship.delegate_fee}/additional)</span>
                        )}
                      </div>
                    )}
                    
                    {sponsorship.benefits && sponsorship.benefits.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-gray-500">Benefits:</p>
                        <div className="flex flex-wrap gap-1">
                          {sponsorship.benefits.slice(0, 4).map((benefit, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {benefit}
                            </Badge>
                          ))}
                          {sponsorship.benefits.length > 4 && (
                            <Badge variant="outline" className="text-xs">
                              +{sponsorship.benefits.length - 4} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Sponsorship Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => { if (!open) resetForm(); setShowAddDialog(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingSponsorship ? 'Edit Sponsorship' : 'Add Sponsorship Package'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Template Selection (only for new sponsorships) */}
            {!editingSponsorship && (
              <div>
                <Label className="mb-2 block">Quick Start - Select a Template</Label>
                <div className="grid grid-cols-4 gap-2">
                  {sponsorshipTemplates.map((template) => {
                    const Icon = template.icon;
                    const isSelected = selectedTemplate?.type === template.type;
                    
                    return (
                      <button
                        key={template.type}
                        onClick={() => handleSelectTemplate(template)}
                        className={`p-3 rounded-lg border text-center transition-all ${
                          isSelected 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Icon size={20} className={`mx-auto mb-1 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} />
                        <p className="text-xs font-medium">{template.name}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Form Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sponsorship Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Gold Sponsor"
                />
              </div>
              <div>
                <Label>Price ($) *</Label>
                <Input
                  type="number"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
                  min="0"
                />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Brief description of this sponsorship package"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Quantity Available</Label>
                <Input
                  type="number"
                  value={form.quantity_available}
                  onChange={(e) => setForm({ ...form, quantity_available: parseInt(e.target.value) || 1 })}
                  min="1"
                />
              </div>
              <div>
                <Label>Delegate Passes Included</Label>
                <Input
                  type="number"
                  value={form.delegate_slots}
                  onChange={(e) => setForm({ ...form, delegate_slots: parseInt(e.target.value) || 0 })}
                  min="0"
                />
              </div>
              <div>
                <Label>Additional Delegate Fee ($)</Label>
                <Input
                  type="number"
                  value={form.delegate_fee}
                  onChange={(e) => setForm({ ...form, delegate_fee: parseFloat(e.target.value) || 0 })}
                  min="0"
                />
              </div>
            </div>

            {/* Benefits */}
            <div>
              <Label>Benefits</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={newBenefit}
                  onChange={(e) => setNewBenefit(e.target.value)}
                  placeholder="Add a benefit"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddBenefit())}
                />
                <Button type="button" variant="outline" onClick={handleAddBenefit}>
                  <Plus size={16} />
                </Button>
              </div>
              {form.benefits.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.benefits.map((benefit, idx) => (
                    <Badge key={idx} variant="secondary" className="pr-1">
                      {benefit}
                      <button 
                        onClick={() => handleRemoveBenefit(idx)}
                        className="ml-1 hover:text-red-600"
                      >
                        <X size={12} />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Active Toggle */}
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                className="w-4 h-4"
              />
              <span>Active (visible to members)</span>
            </label>

            {/* Actions */}
            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button variant="outline" onClick={() => { resetForm(); setShowAddDialog(false); }}>
                Cancel
              </Button>
              <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700 text-white">
                {editingSponsorship ? 'Update Sponsorship' : 'Add Sponsorship'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventSponsorshipManager;
